import { useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { RefreshCw, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const PATH_LABELS: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/brief": "Morning Brief",
  "/signals": "Signals",
  "/portfolio": "Portfolio",
  "/watchlist": "Watchlist",
  "/research": "Research",
  "/options": "Options",
  "/backtests": "Backtests",
  "/market": "Market Map",
  "/macro": "Macro",
  "/reports": "Reports",
  "/system": "System",
  "/dev": "Console",
  "/invites": "Invites",
};

function getPageLabel(pathname: string): string {
  // Exact match first
  if (PATH_LABELS[pathname]) return PATH_LABELS[pathname];
  // Prefix match (e.g. /research/AAPL → Research)
  for (const [path, label] of Object.entries(PATH_LABELS)) {
    if (pathname.startsWith(path)) return label;
  }
  return "Midas";
}

interface QuickActionBarProps {
  onSearchClick: () => void;
}

export function QuickActionBar({ onSearchClick }: QuickActionBarProps) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [spinning, setSpinning] = useState(false);

  const pageLabel = getPageLabel(location.pathname);

  // Ticker from URL for research pages
  const tickerMatch = location.pathname.match(/^\/research\/([A-Z]+)/i);
  const ticker = tickerMatch?.[1]?.toUpperCase();

  // Relative time display
  const [timeStr, setTimeStr] = useState("just now");
  useEffect(() => {
    const update = () => {
      const sec = Math.floor((Date.now() - lastRefresh) / 1000);
      if (sec < 10) setTimeStr("just now");
      else if (sec < 60) setTimeStr(`${sec}s ago`);
      else setTimeStr(`${Math.floor(sec / 60)}m ago`);
    };
    update();
    const interval = setInterval(update, 10_000);
    return () => clearInterval(interval);
  }, [lastRefresh]);

  const handleRefresh = () => {
    setSpinning(true);
    queryClient.invalidateQueries();
    setLastRefresh(Date.now());
    setTimeout(() => setSpinning(false), 600);
  };

  return (
    <div className="flex items-center justify-between gap-3 mb-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs text-zinc-600 font-medium hidden sm:inline">Midas</span>
        <span className="text-xs text-zinc-700 hidden sm:inline">/</span>
        <span className="text-xs font-semibold text-zinc-400 truncate">{pageLabel}</span>
        {ticker && (
          <>
            <span className="text-xs text-zinc-700">/</span>
            <span className="text-xs font-mono font-bold text-amber-400">{ticker}</span>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Search button — mobile only (desktop has sidebar button + Ctrl+K) */}
        <button
          onClick={onSearchClick}
          className="md:hidden flex items-center justify-center w-8 h-8 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04] transition-colors"
        >
          <Search size={16} />
        </button>

        {/* Last updated + refresh */}
        <span className="text-[10px] text-zinc-600 font-mono hidden sm:inline">{timeStr}</span>
        <button
          onClick={handleRefresh}
          className="flex items-center justify-center w-8 h-8 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04] transition-colors"
          title="Refresh all data"
        >
          <RefreshCw size={14} className={cn(spinning && "animate-spin")} />
        </button>
      </div>
    </div>
  );
}
